import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Edit,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  CalendarCheck,
} from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { getIntervalDescription } from "@/utils/calendarUtils";
import { UpcomingReportingDates } from "./UpcomingReportingDates";
import {
  useFundCalendarDetailsController,
  FundCalendarDetailsControllerProps,
} from "./useFundCalendarDetailsController";

type FundCalendarDetailsProps = FundCalendarDetailsControllerProps;

export default function FundCalendarDetails(props: FundCalendarDetailsProps) {
  const controller = useFundCalendarDetailsController(props);
  const { calendar } = controller;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>{calendar.name}</CardTitle>
            <CardDescription>Fund Calendar Details</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={controller.onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500">
                Calendar Type
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-primary" />
                <span>
                  {getIntervalDescription(calendar.intervalDefinition)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500">
                Default Status
              </div>
              <div className="flex items-center">
                {calendar.isDefault ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    <span>Default Calendar</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2 text-gray-400" />
                    <span>Not Default</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {calendar.createdAt && (
            <div className="pt-4 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-500">
                    Created
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-sm">
                      {formatDate(calendar.createdAt)}
                    </span>
                  </div>
                </div>
                {calendar.updatedAt && (
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-500">
                      Last Updated
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-sm">
                        {formatDate(calendar.updatedAt)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {controller.selectedReportingPeriod && (
            <div className="pt-4 border-t">
              <div className="flex items-center mb-2">
                <CalendarCheck className="h-4 w-4 mr-2 text-blue-500" />
                <span className="text-sm font-medium">
                  Current Reporting Period:{" "}
                  {controller.selectedReportingPeriod.period}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                This calendar will be used to calculate upcoming reporting dates
                based on the currently selected reporting period.
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter>
          {!calendar.isDefault && (
            <Button variant="outline" onClick={controller.setAsDefault}>
              Set as Default
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Upcoming Reporting Dates based on current calendar */}
      <UpcomingReportingDates calendar={calendar} numberOfDates={6} />
    </div>
  );
}
